import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import flightManagementService from '../../services/flightManagementService';
import aircraftService from '../../services/aircraftService';


// Async thunks - matching PHP implementation only
export const fetchFlights = createAsyncThunk(
  'flightManagement/fetchFlights',
  async (params, { rejectWithValue }) => {
    try {
      const response = await flightManagementService.getFlights(params);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

export const fetchFlightById = createAsyncThunk(
  'flightManagement/fetchFlightById',
  async (id, { rejectWithValue }) => {
    try {
      const response = await flightManagementService.getFlightById(id);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

export const createFlight = createAsyncThunk(
  'flightManagement/createFlight',
  async (data, { rejectWithValue }) => {
    try {
      const response = await flightManagementService.createFlight(data);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

export const updateFlight = createAsyncThunk(
  'flightManagement/updateFlight',
  async ({ id, data }, { rejectWithValue }) => {
    try {
      const response = await flightManagementService.updateFlight(id, data);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

export const deleteFlight = createAsyncThunk(
  'flightManagement/deleteFlight',
  async (id, { rejectWithValue }) => {
    try {
      const response = await flightManagementService.deleteFlight(id);
      return { id, ...response.data };
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

export const restoreFlight = createAsyncThunk(
  'flightManagement/restoreFlight',
  async (id, { rejectWithValue }) => {
    try {
      const response = await flightManagementService.restoreFlight(id);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

export const permanentDeleteFlight = createAsyncThunk(
  'flightManagement/permanentDeleteFlight',
  async (id, { rejectWithValue }) => {
    try {
      const response = await flightManagementService.permanentDeleteFlight(id);
      return { id, ...response.data };
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

export const exportFlights = createAsyncThunk(
  'flightManagement/exportFlights',
  async (format, { rejectWithValue }) => {
    try {
      const response = await flightManagementService.exportFlights(format);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

export const cancelFlight = createAsyncThunk(
  'flightManagement/cancelFlight',
  async (id, { rejectWithValue }) => {
    try {
      const response = await flightManagementService.cancelFlight(id);
      if (response?.success === false) {
        return rejectWithValue(response.message || 'Failed to cancel flight');
      }
      return { id, ...(response?.data || {}) };
    } catch (error) {
      return rejectWithValue(
        error.data?.message || error.message || 'Failed to cancel flight'
      );
    }
  }
);

export const fetchFlightBookings = createAsyncThunk(
  'flightManagement/fetchBookings',
  async (id, { rejectWithValue }) => {
    try {
      const response = await flightManagementService.getFlightBookings(id);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

export const publishFlight = createAsyncThunk(
  'flightManagement/publishFlight',
  async (id, { rejectWithValue }) => {
    try {
      const response = await flightManagementService.publishFlight(id);
      return { id, ...response.data };
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

export const saveDraft = createAsyncThunk(
  'flightManagement/saveDraft',
  async (data, { rejectWithValue }) => {
    try {
      const response = await flightManagementService.saveDraft(data);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

export const rescheduleFlight = createAsyncThunk(
  'flightManagement/rescheduleFlight',
  async ({ id, data }, { rejectWithValue }) => {
    try {
      const response = await flightManagementService.updateFlight(id, data);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);


// Initial state
const initialState = {
  flights: [],
  currentFlight: null,
  bookings: [],
  loading: false,
  error: null,
  pagination: {
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    itemsPerPage: 10
  }
};

// Slice
const flightManagementSlice = createSlice({
  name: 'flightManagement',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    clearCurrentFlight: (state) => {
      state.currentFlight = null;
    },
    setCurrentFlight: (state, action) => {
      state.currentFlight = action.payload;
    }
  },
  extraReducers: (builder) => {
    builder
      // Fetch flights
      .addCase(fetchFlights.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchFlights.fulfilled, (state, action) => {
        state.loading = false;
        state.flights = action.payload.data?.flights || action.payload.flights || action.payload;
        if (action.payload.data?.pagination) {
          state.pagination = action.payload.data.pagination;
        } else if (action.payload.pagination) {
          state.pagination = action.payload.pagination;
        }
      })
      .addCase(fetchFlights.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Fetch single flight
      .addCase(fetchFlightById.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchFlightById.fulfilled, (state, action) => {
        state.loading = false;
        state.currentFlight = action.payload.data || action.payload;
      })
      .addCase(fetchFlightById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Create flight
      .addCase(createFlight.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createFlight.fulfilled, (state, action) => {
        state.loading = false;
        const newFlight = action.payload.data || action.payload;
        state.flights.unshift(newFlight);
      })
      .addCase(createFlight.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Update flight
      .addCase(updateFlight.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateFlight.fulfilled, (state, action) => {
        state.loading = false;
        const updatedFlight = action.payload.data || action.payload;
        const index = state.flights.findIndex(f => f.id === updatedFlight.id);
        if (index !== -1) {
          state.flights[index] = updatedFlight;
        }
        if (state.currentFlight?.id === updatedFlight.id) {
          state.currentFlight = updatedFlight;
        }
      })
      .addCase(updateFlight.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Reschedule flight
      .addCase(rescheduleFlight.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(rescheduleFlight.fulfilled, (state, action) => {
        state.loading = false;
        const updatedFlight = action.payload.data || action.payload;
        const index = state.flights.findIndex(f => f.id === updatedFlight.id);
        if (index !== -1) {
          state.flights[index] = updatedFlight;
        }
        if (state.currentFlight?.id === updatedFlight.id) {
          state.currentFlight = updatedFlight;
        }
      })
      .addCase(rescheduleFlight.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Delete flight
      .addCase(deleteFlight.fulfilled, (state, action) => {
        state.flights = state.flights.filter(f => f.id !== action.payload.id);
      })

      // Restore flight
      .addCase(restoreFlight.fulfilled, (state, action) => {
        const restoredFlight = action.payload.data || action.payload;
        state.flights.unshift(restoredFlight);
      })

      // Permanently delete flight
      .addCase(permanentDeleteFlight.fulfilled, (state, action) => {
        state.flights = state.flights.filter(f => f.id !== action.payload.id);
      })

      // Cancel flight
      .addCase(cancelFlight.fulfilled, (state, action) => {
        const index = state.flights.findIndex(f => f.id === action.payload.id);
        if (index !== -1) {
          state.flights[index].status = 'canceled';
        }
        if (state.currentFlight?.id === action.payload.id) {
          state.currentFlight.status = 'canceled';
        }
      })

      // Fetch flight bookings
      .addCase(fetchFlightBookings.fulfilled, (state, action) => {
        state.bookings = action.payload.data || action.payload;
      })

      // Publish flight
      .addCase(publishFlight.fulfilled, (state, action) => {
        const index = state.flights.findIndex(f => f.id === action.payload.id);
        if (index !== -1) {
          state.flights[index].status = 'published';
        }
        if (state.currentFlight?.id === action.payload.id) {
          state.currentFlight.status = 'published';
        }
      })

      // Save draft
      .addCase(saveDraft.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(saveDraft.fulfilled, (state, action) => {
        state.loading = false;
        const draftFlight = action.payload.data || action.payload;
        state.flights.unshift(draftFlight);
      })
      .addCase(saveDraft.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  }
});

export const { clearError, clearCurrentFlight, setCurrentFlight } = flightManagementSlice.actions;
export default flightManagementSlice.reducer;
